import React from 'react';
import { cn } from '@core/utils/cn';
import { METRIC_TILE_DESIGN } from './constants';

export type SummaryTone = 'neutral' | 'success' | 'error' | 'warning' | 'muted';
export type TileSize = 'lg' | 'md';

const TONE_CLASSES: Record<SummaryTone, string> = {
    neutral: 'text-foreground',
    success: 'text-success',
    error: 'text-destructive',
    warning: 'text-warning',
    muted: 'text-muted-foreground'
};

interface MetricTileProps {
    label: string;
    value: string;
    unit?: string;
    helper?: string;
    tone?: SummaryTone;
    size?: TileSize;
    icon?: React.ElementType;
    iconClassName?: string;
    reserveHelperSpace?: boolean;
    allowValueWrap?: boolean;
}

const { SCALE, FONT_SIZES, ICON_SIZES, PADDING, SPACING } = METRIC_TILE_DESIGN;

export const MetricTile: React.FC<MetricTileProps> = ({
    label,
    value,
    unit,
    helper,
    tone = 'neutral',
    size = 'md',
    icon: LabelIcon,
    iconClassName,
    reserveHelperSpace,
    allowValueWrap
}) => {
    const large = size === 'lg';
    const valueLength = value.replace(/\s+/g, '').length;
    const valueScale = Math.max(
        METRIC_TILE_DESIGN.VALUE_SCALE_MIN,
        1 -
            Math.max(
                0,
                valueLength - METRIC_TILE_DESIGN.VALUE_LENGTH_THRESHOLD
            ) *
                METRIC_TILE_DESIGN.VALUE_SCALE_FACTOR
    );

    const labelFontSize =
        (large ? FONT_SIZES.LABEL_LG : FONT_SIZES.LABEL_MD) * SCALE;
    const valueFontSize =
        (large ? FONT_SIZES.VALUE_LG : FONT_SIZES.VALUE_MD) *
        SCALE *
        valueScale;
    const iconSize = (large ? ICON_SIZES.LG : ICON_SIZES.MD) * SCALE;
    const padding = (large ? PADDING.LG : PADDING.MD) * SCALE * 8;
    const valueSpacing =
        (large ? SPACING.VALUE_LG : SPACING.VALUE_MD) * SCALE * 8;

    return (
        <div
            className="flex h-full flex-col rounded-lg border bg-muted/40"
            style={{ padding }}
        >
            <div className="flex items-center gap-1">
                {LabelIcon && (
                    <LabelIcon
                        className={cn(
                            'shrink-0',
                            iconClassName ?? 'text-muted-foreground'
                        )}
                        style={{ width: iconSize, height: iconSize }}
                    />
                )}
                <span
                    className="font-semibold uppercase text-muted-foreground"
                    style={{
                        fontSize: labelFontSize,
                        letterSpacing: `${METRIC_TILE_DESIGN.LETTER_SPACING * SCALE}em`
                    }}
                >
                    {label}
                </span>
            </div>

            <div
                className="flex items-baseline gap-1"
                style={{ marginTop: valueSpacing }}
            >
                <span
                    className={cn(
                        'font-medium tracking-tight',
                        TONE_CLASSES[tone],
                        allowValueWrap
                            ? 'break-words'
                            : 'overflow-hidden text-ellipsis whitespace-nowrap'
                    )}
                    style={{ fontSize: valueFontSize, lineHeight: 1.15 }}
                >
                    {value}
                </span>
                {unit && (
                    <span
                        className="text-muted-foreground"
                        style={{ fontSize: FONT_SIZES.UNIT * SCALE }}
                    >
                        {unit}
                    </span>
                )}
            </div>

            {(helper || reserveHelperSpace) && (
                <span
                    className={cn(
                        'text-muted-foreground',
                        !helper && 'invisible'
                    )}
                    style={{
                        fontSize: FONT_SIZES.HELPER * SCALE,
                        minHeight: Math.round(
                            METRIC_TILE_DESIGN.HELPER_MIN_HEIGHT * SCALE
                        )
                    }}
                >
                    {helper ?? 'placeholder'}
                </span>
            )}
        </div>
    );
};
